import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { Box, Dialog, DialogContent } from '@mui/material';

import ModalConfirmComponent from './ModalConfirmComponent';

import ErrorBoundary from '../Main/ErrorBoundary';
import ConfirmModal from './ConfirmModal';
import InfoModal from './InfoModal';
import ModalTitle, { MinimizedModalTitle } from './ModalTitle';
import ProgressModal from './ProgressModal';
import PromptUserForValue from './PromptUserForValue';
import { PROGRESS } from './modalTypes';
import withCountdown from './withCountdown';

export const ModalDivider = () => (
  <Box
    sx={(theme) => ({
      borderBottom: `.1rem solid ${theme.palette.grey[300]}`,
      margin: '0rem 2.4rem',
    })}
  />
);

const MODAL_COMPONENTS = {
  CONFIRM: ConfirmModal,
  WITH_COUNTDOWN: withCountdown,
  PROMPT_USER_FOR_VALUE: PromptUserForValue,
  RENDER_PROPS: ({ render, ...rest }) => <div>{render(rest)}</div>,
  INFO: InfoModal,
  PROGRESS: ProgressModal,
  CUSTOM: ({ children }) => children,
};

const ModalHandlerComponent = (props) => {
  const {
    modal: { modalType, modalProps = {}, progressValue = {} },
    onModalClose: closeModal,
    isModalMinimized,
    setIsModalMinimized,
  } = props;

  const {
    title,
    id,
    actions,
    isRTL,
    onConfirm,
    onCancel,
    maxWidth = 'sm',
    isFitContent,
    hideCloseButton,
    isMinimizeAvailable,
    isCloseOnBackdropClick = true,
    customTitleStyle,
    ...rest
  } = modalProps;

  const [isConfirm, setIsConfirm] = useState(false);

  useEffect(() => {
    setIsConfirm(false);
  }, [isMinimizeAvailable]);

  if (!modalType) return null;

  const onConfirmModal = () => setIsConfirm(!isConfirm);

  const onModalClose = () => {
    if (modalType === PROGRESS && !isModalMinimized) onConfirmModal();
    else closeModal();
  };

  const ModalContent = MODAL_COMPONENTS[modalType];
  const onBackdropClick =
    isCloseOnBackdropClick && !isModalMinimized ? closeModal : () => {};
  const progress = progressValue?.[id] ?? 0;

  if (isModalMinimized) {
    return (
      <Dialog
        open
        fullWidth
        disableEscapeKeyDown
        hideBackdrop
        maxWidth={maxWidth}
        onClose={onBackdropClick}
        sx={{ top: 'auto', left: 'auto', width: '300px', height: '54px' }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            backgroundColor: 'transparent',
            width: '100%',
            height: '100%',
            maxHeight: 'fit-content',
            margin: '0',
            overflow: 'hidden',
          },
        }}
      >
        <MinimizedModalTitle
          onClose={onModalClose}
          setIsModalMinimized={setIsModalMinimized}
          progressValue={progress}
          customTitleStyle={customTitleStyle}
        >
          {title}
        </MinimizedModalTitle>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth={maxWidth}
      onClose={onBackdropClick}
      disableEscapeKeyDown
      PaperProps={{
        sx: (theme) => ({
          borderRadius: '10px',
          backgroundColor: theme.palette.common.white,
        }),
      }}
    >
      {!!title && !isConfirm && (
        <ModalTitle
          onClose={onModalClose}
          isMinimizeAvailable={isMinimizeAvailable}
          setIsModalMinimized={setIsModalMinimized}
          customTitleStyle={customTitleStyle}
        >
          {title}
        </ModalTitle>
      )}
      <ModalDivider />

      {isConfirm ? (
        <ModalConfirmComponent
          onModalClose={closeModal}
          onConfirmModal={onConfirmModal}
        />
      ) : (
        <DialogContent sx={{ padding: '2rem' }}>
          <ErrorBoundary>
            <ModalContent
              id={id}
              {...props}
              {...rest}
              onConfirm={(props) => {
                closeModal();
                onConfirm && onConfirm(props); // TODO - review
              }}
              onCancel={() => {
                closeModal();
                onCancel && onCancel();
              }}
            />
          </ErrorBoundary>
        </DialogContent>
      )}
    </Dialog>
  );
};

ModalHandlerComponent.propTypes = {
  modal: PropTypes.instanceOf(Object).isRequired,
  onModalClose: PropTypes.func.isRequired,
  isModalMinimized: PropTypes.bool,
  setIsModalMinimized: PropTypes.func,
};

MODAL_COMPONENTS.CUSTOM.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};
MODAL_COMPONENTS.RENDER_PROPS.propTypes = {
  render: PropTypes.func.isRequired,
};

export default ModalHandlerComponent;
